export abstract class AppError extends Error {
  abstract readonly status: number;
}

export class BadRequestError extends AppError {
  status = 400 as const;
  constructor(msg: string | Record<string, unknown> = "Bad request") {
    super(typeof msg === "string" ? msg : JSON.stringify(msg));
    this.name = "BadRequestError";
  }
}

export class UnauthorizedError extends AppError {
  status = 401 as const;
  constructor(msg = "Unauthorized") {
    super(msg);
    this.name = "UnauthorizedError";
  }
}

export class ForbiddenError extends AppError {
  status = 403 as const;
  constructor(msg = "Forbidden") {
    super(msg);
    this.name = "ForbiddenError";
  }
}

export class NotFoundError extends AppError {
  status = 404 as const;
  constructor(entity: string = "Resource") {
    super(`${entity} not found`);
    this.name = "NotFoundError";
  }
}

export class ConflictError extends AppError {
  status = 409 as const;
  constructor(msg: string) {
    super(msg);
    this.name = "ConflictError";
  }
}

/** 500 whose message is safe to show the caller. A plain `Error` gets masked as
 *  "Internal error" by `fromError`; use this when the message itself is the diagnosis. */
export class InternalError extends AppError {
  status = 500 as const;
  constructor(msg: string) {
    super(msg);
    this.name = "InternalError";
  }
}

/* ------------------------------------------------------------------ */
/* Generic HTTP error for the *client*                                */
/* ------------------------------------------------------------------ */

export class HttpError extends Error {
  constructor(
    public readonly status: number, // status returned by backend
    message: string
  ) {
    super(message);
    this.name = "HttpError";
  }
}

/** Narrow type-guard so we can test err.status without a cast */
export function isHttpError(e: unknown): e is HttpError {
  return (
    e instanceof HttpError ||
    (typeof e === "object" && e !== null && "status" in e && typeof (e as any).status === "number")
  );
}
